import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ChefHat } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";


const defaultForm = {
  name: "",
  email: "",
  password: "",
  language: "nl",
  household_size: "2",
  calorie_target_daily: "2100",
  daily_carb_target: "180",
  preferred_retailer_id: "",
};


export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [form, setForm] = useState(defaultForm);
  const [retailers, setRetailers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);

  const fetchRetailers = useCallback(async () => {
    const response = await api.get("/retailers");
    setRetailers(response.data);
    setForm((current) => (current.preferred_retailer_id || !response.data[0]
      ? current
      : { ...current, preferred_retailer_id: response.data[0].id }));
  }, []);

  useEffect(() => {
    fetchRetailers();
  }, [fetchRetailers]);

  const switchCopy = useMemo(
    () => (
      isSignup
        ? { title: "Maak je Smartly account", subtitle: "Eén mobile-first weekplanner voor je hele week.", cta: "Account maken", alternate: "/login", alternateLabel: "Al een account? Log in" }
        : { title: "Welkom terug", subtitle: "Open direct je planner, recepten en boodschappenlijst.", cta: "Inloggen", alternate: "/signup", alternateLabel: "Nog geen account? Start hier" }
    ),
    [isSignup],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSignup && !termsAccepted) {
      setTermsError(true);
      return;
    }
    setBusy(true);
    setError("");

    try {
      if (isSignup) {
        await signup({
          ...form,
          household_size: Number(form.household_size),
          calorie_target_daily: Number(form.calorie_target_daily),
          daily_carb_target: Number(form.daily_carb_target),
        });
      } else {
        await login({ email: form.email, password: form.password });
      }
      navigate("/");
    } catch (submitError) {
      setError(getApiError(submitError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,209,102,0.34),_transparent_30%),radial-gradient(circle_at_bottom,_rgba(111,168,132,0.22),_transparent_26%),linear-gradient(180deg,_#fbf7ef_0%,_#f4efe3_50%,_#edf3ea_100%)] px-4 sm:px-6"
      style={{
        minHeight: "100dvh",
        paddingTop: "max(1.5rem, env(safe-area-inset-top))",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <div className="mx-auto flex w-full max-w-md flex-col justify-between rounded-[34px] border border-white/80 bg-white/78 p-5 soft-shadow page-enter" style={{ minHeight: "calc(100dvh - max(3rem, env(safe-area-inset-top) + env(safe-area-inset-bottom)))" }}>
        <div>
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-[20px] bg-emerald-700 text-white" data-testid="auth-brand-badge">
            <ChefHat className="h-7 w-7" />
          </div>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700" data-testid="auth-kicker">
            Smartly
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight text-stone-900" data-testid="auth-title">
            {switchCopy.title}
          </h1>
          <p className="mt-3 text-sm text-stone-600" data-testid="auth-subtitle">
            {switchCopy.subtitle}
          </p>

          <form className="mt-7 space-y-4" onSubmit={handleSubmit} data-testid="auth-form" autoComplete="on">
            {isSignup ? (
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="name">
                  Naam
                </label>
                <Input id="name" name="name" autoComplete="name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-name-input" required />
              </div>
            ) : null}

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="email">
                E-mail
              </label>
              <Input id="email" name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="auth-email-input" required />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="password">
                Wachtwoord
              </label>
              <Input id="password" name="password" type="password" autoComplete={isSignup ? "new-password" : "current-password"} minLength={isSignup ? 8 : undefined} maxLength={isSignup ? 72 : undefined} value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="auth-password-input" required />
            </div>

            {!isSignup ? (
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm font-medium text-emerald-700" data-testid="auth-forgot-password-link">
                  Wachtwoord vergeten?
                </Link>
              </div>
            ) : null}

            {isSignup ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
                      Taal
                    </label>
                    <Select value={form.language} onValueChange={(value) => setForm((current) => ({ ...current, language: value }))}>
                      <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-language-select">
                        <SelectValue placeholder="Kies taal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nl">Nederlands</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="household_size">
                      Huishouden
                    </label>
                    <Input id="household_size" name="household_size" type="number" inputMode="numeric" min="1" max="8" value={form.household_size} onChange={(event) => setForm((current) => ({ ...current, household_size: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-household-input" required />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="calorie_target_daily">
                    Dagelijks calorie-doel
                  </label>
                  <Input id="calorie_target_daily" name="calorie_target_daily" type="number" inputMode="numeric" min="1200" max="4500" value={form.calorie_target_daily} onChange={(event) => setForm((current) => ({ ...current, calorie_target_daily: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-calorie-input" required />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="daily_carb_target">
                    Dagelijks koolhydraatdoel (g)
                  </label>
                  <Input id="daily_carb_target" name="daily_carb_target" type="number" inputMode="numeric" min="40" max="400" placeholder="100" value={form.daily_carb_target} onChange={(event) => setForm((current) => ({ ...current, daily_carb_target: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-carb-input" />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">
                    Voorkeursretailer
                  </label>
                  <Select value={form.preferred_retailer_id} onValueChange={(value) => setForm((current) => ({ ...current, preferred_retailer_id: value }))}>
                    <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="signup-retailer-select">
                      <SelectValue placeholder="Kies retailer" />
                    </SelectTrigger>
                    <SelectContent>
                      {retailers.map((retailer) => (
                        <SelectItem key={retailer.id} value={retailer.id}>
                          {retailer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : null}

            {error ? (
              <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="auth-error-message">
                {error}
              </div>
            ) : null}

            {isSignup ? (
              <div className="space-y-2" data-testid="signup-terms-block">
                <label className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white/80 px-3 py-3 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 rounded-md border-stone-300 text-emerald-700 focus:ring-emerald-600"
                    checked={termsAccepted}
                    onChange={(event) => { setTermsAccepted(event.target.checked); if (event.target.checked) setTermsError(false); }}
                    data-testid="signup-terms-checkbox"
                    aria-required="true"
                    aria-invalid={termsError}
                  />
                  <span>
                    Ik ga akkoord met de{" "}
                    <Link to="/terms" target="_blank" rel="noopener" className="font-medium text-emerald-700 hover:text-emerald-800" data-testid="signup-terms-link">Voorwaarden</Link>
                    {" "}en{" "}
                    <Link to="/privacy" target="_blank" rel="noopener" className="font-medium text-emerald-700 hover:text-emerald-800" data-testid="signup-privacy-link">Privacyverklaring</Link>.
                  </span>
                </label>
                {termsError ? (
                  <p className="px-1 text-xs font-medium text-red-700" data-testid="signup-terms-error">
                    Je moet eerst akkoord gaan met de Voorwaarden en Privacyverklaring.
                  </p>
                ) : null}
              </div>
            ) : null}

            <Button type="submit" className="h-12 w-full rounded-full bg-emerald-700 text-white hover:bg-emerald-800" disabled={busy} data-testid="auth-submit-button">
              {busy ? "Even bezig..." : switchCopy.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </div>

        <div className="rounded-[24px] border border-stone-200 bg-white/80 px-4 py-4" data-testid="auth-footer-card">
          <Link to={switchCopy.alternate} className="text-sm font-medium text-emerald-700" data-testid="auth-switch-link">
            {switchCopy.alternateLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}